import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import LANGUAGES from './LANGUAGES';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/Domain/components/Shared/Popover/Popover';
import { cn } from '@/Domain/utils/cn';
import { AISettings } from '@/UI/Identity/ConversationStyle/ConversationStyle.page';

type Props = {
  index: number;
  onDelete: (index: number) => void;
  onChange: (index: number, value: (typeof LANGUAGES)[number]) => void;
  name: string;
  value: (typeof LANGUAGES)[number];
};

export default function LanguageControlComponent({
  index,
  onDelete,
  onChange,
  name,
  value,
}: Props) {
  const { t } = useTranslation(['settings', 'common']);
  const { watch } = useFormContext<AISettings>();
  const languages = watch('languages');
  const isPrimary = index === 0;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const options = useMemo(
    () =>
      LANGUAGES.map((lng) => ({
        label: t(`languages.${lng}`, { ns: 'common' }),
        value: lng,
        disabled: languages.includes(lng) && lng !== value,
      })).sort((a, b) => {
        if (a.value === value) return -1;
        if (b.value === value) return 1;
        return a.label.localeCompare(b.label);
      }),
    [t, languages, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const selected = options.find((o) => o.value === value);

  const handleSelect = (lng: (typeof LANGUAGES)[number]) => {
    onChange(index, lng);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2" data-testid={`language-control-${index}`}>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery('');
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            id={name}
            data-testid={`language-select-${index}`}
            className="flex h-[34px] min-w-0 flex-1 items-center justify-between gap-2 rounded-xl border border-input bg-card px-3 text-sm text-foreground transition-colors data-[state=open]:border-foreground data-[state=open]:ring-[3px] data-[state=open]:ring-ring/25"
          >
            <span className="truncate">{selected?.label}</span>
            <ChevronDown className="size-4 shrink-0 text-foreground-subtle" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="w-[var(--radix-popover-trigger-width)] p-0"
          onOpenAutoFocus={(e) => {
            // Let the Input take focus via autoFocus below
            e.preventDefault();
          }}
        >
          <div className="border-b border-border p-2">
            <Input
              size="sm"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('search', { ns: 'common', defaultValue: 'Search...' })}
              startIcon={<Search className="size-4 text-foreground-subtle" />}
              autoFocus
              aria-label={t('search', { ns: 'common', defaultValue: 'Search' })}
            />
          </div>
          <div className="max-h-64 overflow-auto p-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-foreground-subtle">
                {t('no_results', { ns: 'common', defaultValue: 'No results' })}
              </div>
            ) : (
              filtered.map((option) => {
                const isSelected = option.value === value;
                return (
                  <button
                    type="button"
                    key={option.value}
                    disabled={option.disabled}
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      'flex w-full cursor-pointer items-center justify-between rounded-lg px-2.5 py-2 text-left text-sm font-medium text-foreground outline-none hover:bg-accent focus:bg-accent',
                      'disabled:pointer-events-none disabled:opacity-50',
                      isSelected && 'bg-accent',
                    )}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && <Check className="size-4 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>

      {!isPrimary ? (
        <IconButton
          type="button"
          variant="ghost"
          size="xs"
          onClick={() => onDelete(index)}
          className="shrink-0 text-foreground-subtle hover:text-destructive"
          aria-label={t('languages.remove', { defaultValue: 'Remove language' })}
          data-testid={`language-remove-${index}`}
        >
          <X />
        </IconButton>
      ) : (
        <div className="size-[26px] shrink-0" aria-hidden="true" />
      )}
    </div>
  );
}
